<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveInvitesByTicketStore } from '../../lib/convexRealtime';
  import { createPlusOneInvite } from '../../lib/convexApi';
  import type { InviteRecord } from '../../lib/types';
  import Notice from '../ui/Notice.svelte';

  export let ticketId: string;

  let inviteStore: ReturnType<typeof liveInvitesByTicketStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedTicketId: string | null = null;

  let invites: InviteRecord[] = [];
  let loading = false;
  let error: string | null = null;
  let actionPending = false;
  let autoCreateAttempted = false;

  let copyState: 'idle' | 'copied' | 'error' = 'idle';
  let copyTimer: ReturnType<typeof setTimeout> | null = null;

  function teardown() {
    unsubscribe?.();
    unsubscribe = null;
    inviteStore?.dispose();
    inviteStore = null;
    subscribedTicketId = null;
  }

  $: if (ticketId && ticketId !== subscribedTicketId) {
    teardown();
    subscribedTicketId = ticketId;
    autoCreateAttempted = false;
    inviteStore = liveInvitesByTicketStore(ticketId);
    unsubscribe = inviteStore.subscribe(state => {
      invites = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
  }

  $: activeInvite = invites.find(invite => invite.status !== 'revoked') ?? invites[0] ?? null;
  $: inviteLink = activeInvite
    ? `${window.location.origin}?invite=${encodeURIComponent(activeInvite.code)}`
    : '';
  $: usageLabel = activeInvite ? `${activeInvite.usedCount} of ${activeInvite.maxUses} claimed` : null;

  // First visit for an invite-enabled ticket: create the guest's plus-one link once.
  $: if (
    subscribedTicketId &&
    !loading &&
    !actionPending &&
    !autoCreateAttempted &&
    (!activeInvite || activeInvite.status === 'revoked')
  ) {
    autoCreateAttempted = true;
    void createInvite();
  }

  async function createInvite() {
    if (!subscribedTicketId) return;
    actionPending = true;
    error = null;
    try {
      const result = await createPlusOneInvite(subscribedTicketId);
      if (!result) throw new Error('Invite could not be created');
      await inviteStore?.refresh();
    } catch (err) {
      error = err instanceof Error ? err.message : 'Unable to create invite';
    } finally {
      actionPending = false;
    }
  }

  async function copyLink() {
    if (!inviteLink) return;
    if (copyTimer) clearTimeout(copyTimer);
    try {
      await navigator.clipboard.writeText(inviteLink);
      copyState = 'copied';
    } catch (err) {
      console.error('Copy failed', err);
      copyState = 'error';
    }
    copyTimer = setTimeout(() => {
      copyState = 'idle';
      copyTimer = null;
    }, 2000);
  }

  onDestroy(() => {
    teardown();
    if (copyTimer) clearTimeout(copyTimer);
  });
</script>

<section class="invites surface surface--sunken" aria-labelledby="invites-heading">
  <header>
    <h3 id="invites-heading">Bring a friend</h3>
    <p class="invites__hint">Share your personal invite link. Whoever opens it gets their own ticket.</p>
  </header>

  {#if error}
    <Notice variant="error">{error}</Notice>
  {:else if activeInvite}
    <div class="invites__link-row">
      <code class="invites__link">{inviteLink}</code>
      <button type="button" class="secondary invites__copy" on:click={copyLink}>
        {copyState === 'copied' ? 'Copied ✓' : copyState === 'error' ? 'Copy failed' : 'Copy link'}
      </button>
    </div>
    {#if usageLabel}
      <p class="invites__usage" aria-live="polite">{usageLabel}</p>
    {/if}
  {:else if loading || actionPending}
    <p class="invites__usage">Preparing your invite link…</p>
  {:else}
    <button type="button" class="secondary" on:click={createInvite}>Create invite link</button>
  {/if}
</section>

<style>
  .invites {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
  }

  .invites header h3 {
    margin: 0;
    font-size: var(--font-size-md);
  }

  .invites__hint {
    margin: var(--space-1) 0 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }

  .invites__link-row {
    display: flex;
    gap: var(--space-3);
    align-items: stretch;
    flex-wrap: wrap;
  }

  .invites__link {
    flex: 1 1 240px;
    min-width: 0;
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    background: var(--color-surface-900);
    border: var(--border-subtle);
    font-family: var(--font-mono);
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    overflow-wrap: anywhere;
  }

  .invites__copy {
    flex-shrink: 0;
    align-self: center;
  }

  .invites__usage {
    margin: 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }
</style>
